/**
 * Hook pour recuperer la liste des abonnements (admin)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminSubscription {
  id: string;
  entreprise_id: string;
  entreprise_nom: string;
  plan: string;
  billing_period: string | null;
  status: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
}

interface UseAdminSubscriptionsOptions {
  page?: number;
  pageSize?: number;
  statusFilter?: string;
}

interface SubscriptionStats {
  totalMRR: number;
  activeCount: number;
  trialingCount: number;
  pastDueCount: number;
  canceledCount: number;
  byPlan: Record<string, number>;
}

export function useAdminSubscriptions(options: UseAdminSubscriptionsOptions = {}) {
  const { page = 1, pageSize = 20, statusFilter = '' } = options;

  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchSubscriptions();
  }, [page, pageSize, statusFilter]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Recuperer toutes les subscriptions pour les stats
      const { data: allSubs } = await supabase
        .from('subscriptions')
        .select('plan, billing_period, status');

      // Calculer les stats
      const planPrices: Record<string, { monthly: number; yearly: number }> = {
        free: { monthly: 0, yearly: 0 },
        artisan: { monthly: 29, yearly: 290 },
        pro: { monthly: 79, yearly: 790 },
        entreprise: { monthly: 199, yearly: 1990 },
      };

      let totalMRR = 0;
      let activeCount = 0;
      let trialingCount = 0;
      let pastDueCount = 0;
      let canceledCount = 0;
      const byPlan: Record<string, number> = {};

      (allSubs || []).forEach((sub) => {
        // Compter par statut
        switch (sub.status) {
          case 'active':
            activeCount++;
            break;
          case 'trialing':
            trialingCount++;
            break;
          case 'past_due':
            pastDueCount++;
            break;
          case 'canceled':
            canceledCount++;
            break;
        }

        // Compter par plan
        byPlan[sub.plan] = (byPlan[sub.plan] || 0) + 1;

        // Calculer MRR (seulement actifs et trials)
        if (sub.status === 'active' || sub.status === 'trialing') {
          const prices = planPrices[sub.plan] || { monthly: 0, yearly: 0 };
          if (sub.billing_period === 'yearly') {
            totalMRR += prices.yearly / 12;
          } else {
            totalMRR += prices.monthly;
          }
        }
      });

      setStats({
        totalMRR: Math.round(totalMRR),
        activeCount,
        trialingCount,
        pastDueCount,
        canceledCount,
        byPlan,
      });

      // Construire la requete paginee
      let query = supabase
        .from('subscriptions')
        .select(`
          id,
          entreprise_id,
          plan,
          billing_period,
          status,
          stripe_customer_id,
          stripe_subscription_id,
          current_period_end,
          cancel_at_period_end,
          created_at,
          entreprises(nom)
        `, { count: 'exact' });

      // Filtre par statut
      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data, count, error: queryError } = await query;

      if (queryError) throw queryError;

      // Transformer les donnees
      const transformedSubs: AdminSubscription[] = (data || []).map((sub: any) => ({
        id: sub.id,
        entreprise_id: sub.entreprise_id,
        entreprise_nom: sub.entreprises?.nom || 'Entreprise inconnue',
        plan: sub.plan,
        billing_period: sub.billing_period,
        status: sub.status,
        stripe_customer_id: sub.stripe_customer_id,
        stripe_subscription_id: sub.stripe_subscription_id,
        current_period_end: sub.current_period_end,
        cancel_at_period_end: sub.cancel_at_period_end,
        created_at: sub.created_at,
      }));

      setSubscriptions(transformedSubs);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('[useAdminSubscriptions] Erreur:', err);
      setError(err instanceof Error ? err : new Error('Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return {
    subscriptions,
    stats,
    totalCount,
    totalPages: Math.ceil(totalCount / pageSize),
    loading,
    error,
    refetch: fetchSubscriptions,
  };
}
